// The start page for signed-out visitors (spec §14.4): what Atomic Lab does and
// why it's worth it, start drawing without an account (optionally from a
// template), the boards already kept in this browser, and the prices.
import type { BoardObject } from '../../shared/types.ts';
import { formatBytes, formatPrice, FREE_STORAGE_BYTES, TEAM_PRICE, TRIAL_DAYS } from '../../shared/billing.ts';
import { TEMPLATES, templateObjects } from '../../shared/templates.ts';
import { ImageCache } from '../engine/images.ts';
import { renderThumbnail } from '../export/scene.ts';
import { deleteLocalBoard, listLocalBoards, localBoardsSoon, onLocalBoardChange, type LocalBoardInfo } from '../local/boards.ts';
import { canvasMeasure, TextLayouts } from '../text/placement.ts';
import { h } from '../util/dom.ts';
import { icons } from './icons.ts';
import { logoMark } from './logo.ts';
import { legalLinks, mount, PRODUCT, relativeTime } from './page.ts';
import { withSource } from '../analytics.ts';
// Screenshots of the real app (scripts/marketing-images.mjs); the rows share the blog's.
import heroImage from '../landing/hero.webp';
import kanbanImage from '../../content/blog/images/kanban-drag.jpg';
import publishImage from '../../content/blog/images/publish-to-the-web.jpg';
import speedImage from '../../content/blog/images/speed-zoomed-out.jpg';

const LOCAL_WAIT_MS = 300;

/** Things we can stand behind (spec §10, §15.2). */
const FACTS: [string, string][] = [
  ['10,000', 'objects on a board, and it’s built to stay smooth'],
  ['< 100 KB', 'to download, so boards open in moments'],
  ['1 cookie', 'to keep you signed in. No adverts, no trackers'],
  ['Every feature', 'on every plan, Free included'],
];

const ROWS: { kicker: string; title: string; text: string; image: string; alt: string; more: [string, string] }[] = [
  {
    kicker: 'Plan', title: 'From sticky notes to a plan, on one board',
    text: 'Brainstorm with sticky notes, then turn the best ideas into cards with checklists, people and due dates. Drop them into Kanban columns that stack and snap, and drag a card across to move it on. The plan stays right next to the thinking behind it.',
    image: kanbanImage, alt: 'Dragging a card between Kanban columns, with a gap opening where it will land',
    more: ['/blog/kanban-columns', 'How Kanban columns work'],
  },
  {
    kicker: 'Share', title: 'Show anyone, no account needed',
    text: 'Publish a board and send the link. Clients and stakeholders see it live and read-only, without signing up, and never see who’s editing. Replace the link or turn it off whenever you like.',
    image: publishImage, alt: 'A published board open in a browser, marked View only',
    more: ['/blog/publish-to-the-web', 'Publishing to the web'],
  },
  {
    kicker: 'Speed', title: 'Fast on big boards and ordinary laptops',
    text: 'Atomic Lab only draws what you can see, shows your own changes straight away, and sends them to everyone else in the background. The board keeps up in the workshop, not just in the demo.',
    image: speedImage, alt: 'A big board of Kanban columns, brainstorms, retrospectives and flowcharts, zoomed right out',
    more: ['/blog/speed-is-a-feature', 'Why speed matters'],
  },
];

const INCLUDED: [string, string, string][] = [
  [icons.connector, 'Notes, shapes and connectors', 'Sketch and map ideas with sticky notes, shapes, text, a pen and connectors.'],
  [icons.card, 'Cards', 'Checklists, people and due dates, with progress at a glance.'],
  [icons.list, 'Kanban columns', 'Cards stack and snap into place as you drag them.'],
  [icons.select, 'Live together', 'See everyone’s cursors and edits as they happen.'],
  [icons.frame, 'Templates', 'Start from a Kanban board, retrospective, brainstorm or flowchart.'],
  [icons.undo, 'Version history', 'Look back at earlier versions of a board and restore one.'],
  [icons.image, 'Images and export', 'Drop in images, and download any board as PNG or SVG.'],
  [icons.mic, 'Audio clips', 'Record a voice note or add an audio file, and play it right on the board.'],
];

export async function landingPage(): Promise<void> {
  document.title = `${PRODUCT} · A fast, simple online whiteboard`;
  // Sign-ups are credited to where this visit came from (spec §15.2).
  const signupHref = withSource('/login?signup=1');
  const startDrawing = (cls = 'btn primary large') => h('a', { class: cls, href: '/local/new' }, ['Start drawing']);
  const signUp = (cls = 'btn large', label = 'Sign up free') => h('a', { class: cls, href: signupHref }, [label]);

  const header = h('header', { class: 'app-header landing-header' }, [
    h('a', { class: 'brand', href: '/', 'aria-label': `${PRODUCT} home` }, [logoMark(24), h('span', { class: 'brand-name' }, [PRODUCT])]),
    h('div', { class: 'spacer' }),
    h('a', { class: 'nav-link nav-extra', href: '#pricing' }, ['Pricing']),
    h('a', { class: 'nav-link nav-extra', href: '/blog' }, ['Blog']),
    h('a', { class: 'nav-link', href: '/login' }, ['Sign in']),
    h('a', { class: 'btn small primary', href: signupHref }, ['Sign up free']),
  ]);

  const hero = h('section', { class: 'landing-hero' }, [
    h('h1', {}, ['The whiteboard that keeps up with your team']),
    h('p', { class: 'landing-lead' }, ['Brainstorm on sticky notes, map out the flow, then plan the work in Kanban columns, all on one fast, infinite canvas that everyone can edit at once.']),
    h('div', { class: 'landing-actions' }, [startDrawing(), signUp()]),
    h('ul', { class: 'landing-checks', role: 'list' }, [
      h('li', {}, ['No sign-up to start']),
      h('li', {}, ['Free for individuals']),
      h('li', {}, [`${TRIAL_DAYS}-day Team trial, no card needed`]),
    ]),
  ]);
  const shot = h('figure', { class: 'landing-shot' }, [
    h('img', {
      src: heroImage, width: '1280', height: '720', fetchpriority: 'high', decoding: 'async',
      alt: 'An Atomic Lab board: Kanban columns of cards beside sticky-note ideas, with Mia and Sam working on it at the same time',
    }),
  ]);

  const facts = h('section', { class: 'landing-facts', 'aria-label': 'Atomic Lab in numbers' }, FACTS.map(([big, small]) =>
    h('div', {}, [h('strong', {}, [big]), h('span', {}, [small])])));

  const rows = h('section', { class: 'landing-rows' }, ROWS.map((r) => h('div', { class: 'landing-row' }, [
    h('div', { class: 'landing-row-text' }, [
      h('p', { class: 'landing-kicker' }, [r.kicker]),
      h('h2', {}, [r.title]),
      h('p', {}, [r.text]),
      h('a', { class: 'landing-more', href: r.more[0] }, [`${r.more[1]} →`]),
    ]),
    h('img', { src: r.image, alt: r.alt, width: '1200', height: '630', loading: 'lazy', decoding: 'async' }),
  ])));

  const layouts = new TextLayouts(canvasMeasure);
  const images = new ImageCache();
  const templates = h('ul', { class: 'template-grid landing-templates', role: 'list' }, TEMPLATES.filter((t) => t.id !== 'blank').map((t) => {
    const canvas = h('canvas', { class: 'template-preview', 'aria-hidden': 'true' });
    const objs = templateObjects(t.id, 'preview');
    const byId = new Map<string, BoardObject>(objs.map((o) => [o.id, o]));
    requestAnimationFrame(() => renderThumbnail(canvas, objs, { get: (id) => byId.get(id), layouts, images }, '#F7F7F5'));
    return h('li', {}, [h('a', { class: 'template-card', href: `/local/new?template=${t.id}` }, [
      canvas, h('strong', {}, [t.name]), h('span', { class: 'muted' }, [t.description]),
    ])]);
  }));

  const included = h('section', { class: 'landing-included' }, [
    h('h2', {}, ['Everything you need, nothing you don’t']),
    h('ul', { role: 'list' }, INCLUDED.map(([icon, title, text]) => {
      const mark = h('span', { class: 'landing-icon' });
      mark.innerHTML = icon;
      return h('li', {}, [mark, h('h3', {}, [title]), h('p', { class: 'muted' }, [text])]);
    })),
  ]);

  const priceList = (items: string[]) => h('ul', { class: 'price-list', role: 'list' }, items.map((t) => h('li', {}, [t])));
  // A year costs less than twelve months: say how much, in months where it's a whole number.
  const saved = 12 * TEAM_PRICE.month - TEAM_PRICE.year;
  const yearly = `or ${formatPrice(TEAM_PRICE.year)} a year` + (saved > 0 ? ` (${saved % TEAM_PRICE.month ? `save ${formatPrice(saved)}` : `${saved / TEAM_PRICE.month} months free`})` : '');
  const pricing = h('section', { class: 'landing-pricing', id: 'pricing' }, [
    h('h2', {}, ['Simple, fair pricing']),
    h('p', { class: 'landing-section-lead' }, ['Every feature is on every plan. You only pay when your team edits together.']),
    h('div', { class: 'price-cards' }, [
      h('div', { class: 'price-card' }, [
        h('h3', {}, ['Free']),
        h('p', { class: 'price-for' }, ['For working on your own']),
        h('p', { class: 'price' }, [formatPrice(0), h('small', {}, [' / month'])]),
        h('p', { class: 'price-sub' }, ['No time limit, no card']),
        priceList(['Unlimited boards', 'Every tool, template and export', 'Version history', 'Publish boards to the web', `${formatBytes(FREE_STORAGE_BYTES)} for images and audio`, 'People you add can view your boards']),
        signUp('btn large'),
      ]),
      h('div', { class: 'price-card featured' }, [
        h('p', { class: 'price-badge' }, [`${TRIAL_DAYS}-day free trial`]),
        h('h3', {}, ['Team']),
        h('p', { class: 'price-for' }, ['For working together']),
        h('p', { class: 'price' }, [formatPrice(TEAM_PRICE.month), h('small', {}, [' per member / month'])]),
        h('p', { class: 'price-sub' }, [yearly]),
        priceList(['Everything in Free', 'Everyone in the workspace edits', 'Guests are free, on just the boards they need', 'No storage limit', 'No “Made with Atomic Lab” badge on published boards']),
        signUp('btn primary large', 'Start your free trial'),
        h('p', { class: 'price-note' }, ['The trial starts when you invite your first teammate. No card needed.']),
      ]),
    ]),
    h('p', { class: 'price-small' }, [
      'Prices in US dollars. A member is anyone in the workspace who isn’t a guest, you included. If a Team plan ends, its boards become read-only: nothing is deleted.',
    ]),
  ]);

  const FAQ: [string, (Node | string)[]][] = [
    ['Do I need an account to try it?', ['No. Choose Start drawing and you’re on a board straight away. It’s kept in your browser; sign up free when you want to keep it safe or share it, and it moves into your account.']],
    ['What do I get on Free?', ['Unlimited boards and every feature, for one person editing. People you add to a Free workspace can view its boards. When you want them to edit too, that’s Team.']],
    ['Who counts as a member?', ['Everyone in the workspace who isn’t a guest, you included. Guests, people you add to just the boards they need, are always free.']],
    ['How does the trial work?', [`Inviting your first teammate to edit starts a ${TRIAL_DAYS}-day Team trial, with no card needed. Subscribe monthly or yearly whenever you’re ready.`]],
    ['What happens if we stop paying?', ['Your boards become read-only. Nothing is deleted: everyone can still open and export them, and you can subscribe again or move back to Free.']],
    ['Can I get my data out?', ['Yes. Export any board as PNG or SVG, or download everything in your account, images included, as a zip. You can delete your account yourself at any time.']],
    ['Do you track me?', ['No. There’s one cookie, to keep you signed in, and no adverts. We count page views ourselves, without cookies or third-party analytics. The ', h('a', { href: '/privacy' }, ['privacy policy']), ' has the details.']],
  ];
  const faq = h('section', { class: 'landing-faq' }, [
    h('h2', {}, ['Questions']),
    ...FAQ.map(([q, a]) => h('details', {}, [h('summary', {}, [q]), h('p', {}, a)])),
  ]);

  const closing = h('section', { class: 'landing-closing' }, [
    h('h2', {}, ['Get your team on the same board']),
    h('p', {}, ['Start drawing in seconds. No account, no card.']),
    h('div', { class: 'landing-actions' }, [startDrawing(), signUp()]),
  ]);

  // Boards kept in this browser: drawn with the page if IndexedDB answers quickly (as it normally
  // does), so nothing shifts as you go to click; otherwise added when it does. It's never waited on.
  const localSection = h('section', { class: 'landing-section', hidden: '' });
  const local = await localBoardsSoon(LOCAL_WAIT_MS, renderLocal);
  if (local) renderLocal(local);
  onLocalBoardChange(() => void listLocalBoards().then(renderLocal, () => {}));

  mount(header, h('main', { class: 'page landing' }, [
    hero,
    shot,
    localSection,
    facts,
    rows,
    h('section', { class: 'landing-section' }, [h('h2', {}, ['Start from a template']), templates]),
    included,
    pricing,
    faq,
    closing,
    legalLinks([h('a', { href: '/blog' }, ['Blog'])]),
  ]));
  // Links to /#pricing (from the blog) arrive before the page is drawn, so the browser can't scroll there itself.
  if (location.hash.length > 1) document.getElementById(location.hash.slice(1))?.scrollIntoView();

  function renderLocal(boards: LocalBoardInfo[]): void {
    localSection.hidden = !boards.length;
    const grid = h('ul', { class: 'board-grid', role: 'list' }, boards.map((b) => {
      const item = h('li', { class: 'board-card' }, [
        h('a', { href: `/local/${b.id}`, class: 'board-link' }, [
          h('div', { class: 'board-thumb', 'aria-hidden': 'true' }),
          h('div', { class: 'board-meta' }, [h('strong', {}, [b.title]), h('span', { class: 'muted' }, [`Edited ${relativeTime(b.updatedAt)}`])]),
        ]),
      ]);
      const remove = h('button', { type: 'button', class: 'card-action', 'aria-label': `Delete ${b.title}`, title: 'Delete board' }, ['Delete']);
      remove.addEventListener('click', () => {
        if (confirm(`Delete "${b.title}" from this browser? This can't be undone.`)) void deleteLocalBoard(b.id);
      });
      item.append(remove);
      return item;
    }));
    localSection.replaceChildren(
      h('div', { class: 'page-head' }, [
        h('h2', {}, ['Boards in this browser']),
        h('a', { class: 'btn small', href: signupHref }, ['Sign up to keep them safe']),
      ]),
      h('p', { class: 'muted landing-note' }, ['These live only in this browser, which can clear them. Sign up free to keep them and share them; they move into your account.']),
      grid,
    );
  }
}
